/**
 * Shared leave / permanent-purge helpers for fullscreen + dock float panels.
 *
 * leave  → DELETE /chat/v2/rooms/:id          (membership leave)
 * purge  → DELETE /chat/v2/rooms/:id/purge    (destroy room for everyone)
 */
import { batch } from '@preact/signals'
import { chatApi } from './chat-api.js'
import { t } from './chat-i18n.js'

function scrubRoom(store, cable, roomId) {
  if (!store) return
  batch(() => {
    store.rooms.value = (store.rooms.value ?? []).filter(r => r.id !== roomId)

    const msgs = { ...(store.messagesByRoom.value ?? {}) }
    delete msgs[roomId]
    store.messagesByRoom.value = msgs

    const more = { ...(store.hasMoreByRoom.value ?? {}) }
    delete more[roomId]
    store.hasMoreByRoom.value = more

    const oldest = { ...(store.oldestIdByRoom.value ?? {}) }
    delete oldest[roomId]
    store.oldestIdByRoom.value = oldest

    if (store.floatPanels) {
      store.floatPanels.value = (store.floatPanels.value ?? []).filter(p => p.roomId !== roomId)
    }

    if (store.activeRoomId.value === roomId) {
      if (typeof store.navigateToRoom === 'function') {
        store.navigateToRoom(null)
      } else {
        store.activeRoomId.value = null
      }
    }
  })
  cable?.unsubscribeRoom?.(roomId)
}

function scrubAll({ store, cable, roomId, dockStore }) {
  scrubRoom(store, cable, roomId)
  if (dockStore && dockStore !== store) scrubRoom(dockStore, null, roomId)
}

/**
 * @returns {Promise<boolean>} true when leave succeeded
 */
export async function leaveRoom({ store, cable, roomId, dockStore }) {
  if (!roomId) return false
  if (!window.confirm(t('chat.rooms.delete_confirm'))) return false

  try {
    const res = await chatApi.rooms.destroy(roomId)
    if (!res.ok) return false
  } catch {
    return false
  }

  scrubAll({ store, cable, roomId, dockStore })
  return true
}

/**
 * Permanently delete the room for every member.
 * @returns {Promise<boolean>}
 */
export async function purgeRoom({ store, cable, roomId, dockStore }) {
  if (!roomId) return false
  if (!window.confirm(t('chat.rooms.purge_confirm'))) return false

  try {
    const res = await chatApi.rooms.purge(roomId)
    if (!res.ok) return false
  } catch {
    return false
  }

  scrubAll({ store, cable, roomId, dockStore })
  return true
}

/** Apply a remote room_deleted cable event to the local store. */
export function applyRoomDeleted(store, cable, roomId) {
  if (!roomId || !store) return
  scrubAll({ store, cable, roomId })
}
